import { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  FormControl,
  FormLabel,
  Select,
  Badge,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useSuppliers } from "../hooks/useSuppliers";
import { useForm } from "react-hook-form";
import { useSourcedProducts } from "../hooks/useProduceType";
import { format } from "date-fns";
import { useCancelSourcingProduct } from "../hooks/useSourcingCancellation";
import { useResendSourcingEmail } from "../hooks/useSourcingEmail";

const CancelSourcingModal = ({ record, isOpen, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const toast = useToast();
  const { mutate, isLoading } = useCancelSourcingProduct();

  const handleConfirm = () => {
    mutate(
      { id: record.id, notes: reason },
      {
        onSuccess: () => {
          toast({
            title: "Sourcing product cancelled",
            description:
              "The sourcing product has been successfully cancelled.",
            status: "success",
          });
          onDone();
          onClose();
        },
        onError: (err) => {
          toast({
            title: "Unable to cancel",
            description: err?.response?.data?.message,
            status: "error",
          });
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cancel LPO {record?.lpo_number}?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p style={{ marginBottom: 12 }}>
            This will notify finance to stop payment for this LPO. This can't be
            undone.
          </p>
          <FormControl isRequired>
            <FormLabel>Reason</FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being cancelled?"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Back
          </Button>
          <Button
            colorScheme="red"
            isDisabled={!reason.trim()}
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            Confirm Cancellation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const History = () => {
  const [offset] = useState("&offset=0");
  const [activeRecord, setActiveRecord] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const resendEmail = useResendSourcingEmail();
  const { data: supplier } = useSuppliers();
  const {
    register,

    watch,
    formState: { errors },
  } = useForm();
  const formFilters = watch("supplier_id");
  const { data: products } = useSourcedProducts(
    formFilters ? `&supplier_id=${formFilters}` : ``,
    offset,
  );

  return (
    <Box>
      <FormControl isInvalid={errors?.name?.message}>
        <FormLabel>Supplier Name</FormLabel>
        <Select
          placeholder="Select agent..."
          name="supplier_id"
          focusBorderColor="brand.800"
          {...register("supplier_id", { required: "Supplier is required" })}
        >
          {supplier
            ?.sort((a, b) => a.display_name.localeCompare(b.display_name))
            .map((supplier) => (
              <option key={supplier.id} value={supplier?.id}>
                {supplier?.display_name}
              </option>
            ))}
        </Select>
      </FormControl>
      <TableContainer mt="2">
        <Table size="md" variant="striped">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>LPO NUmber</Th>
              <Th>Produce Type</Th>
              <Th>Quality Score</Th>
              <Th>Net Weight</Th>
              <Th>Cost/Kg</Th>
              <Th>Total</Th>
              <Th>Status</Th>
              <Th>Email</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {products?.returned_resultset?.map((product) => (
              <Tr key={product.id}>
                <Td>
                  {format(new Date(product.created_at), "dd MMM yyyy HH:mm a")}
                </Td>
                <Td>{product.lpo_number}</Td>
                <Td>{product.product_type?.name}</Td>
                <Td>{product.quality_score}</Td>
                <Td>{product.total_net_weight}</Td>
                <Td>{product.cost_per_kg}</Td>
                <Td>{product.total_cost}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      product.status === "cancelled" ? "red" : "green"
                    }
                  >
                    {product.status}
                  </Badge>
                </Td>
                <Td>
                  <Badge
                    colorScheme={
                      product.email_status === "sent"
                        ? "green"
                        : product.email_status === "failed"
                          ? "red"
                          : "gray"
                    }
                    title={product.email_error || ""}
                  >
                    {product.email_status || "unknown"}
                  </Badge>
                </Td>
                <Td>
                  {product.email_status === "failed" &&
                    product.status !== "cancelled" && (
                      <Button
                        size="sm"
                        mr="2"
                        variant="outline"
                        colorScheme="orange"
                        isLoading={
                          resendEmail.isLoading &&
                          resendEmail.variables === product.id
                        }
                        onClick={() => resendEmail.mutate(product.id)}
                      >
                        Resend email
                      </Button>
                    )}
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    isDisabled={product.status === "cancelled"}
                    onClick={() => {
                      setActiveRecord(product);
                      onOpen();
                    }}
                  >
                    Cancel
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {activeRecord && (
        <CancelSourcingModal
          record={activeRecord}
          isOpen={isOpen}
          onClose={onClose}
          onDone={() => setActiveRecord(null)}
        />
      )}
    </Box>
  );
};

export default History;